import { computed } from 'vue'
// types
import type { ComputedRef } from 'vue'
import type { BlockSectionLayoutProps } from '@ts-blocks/block-section-layout/interfaces'
import type { BlockCardIconProps } from '@ts-blocks/block-card-icon/interfaces'
import type { BlockCardImpactProps } from '@ts-blocks/block-card-impact/interfaces'
import type { SectionImpactCardsProps } from '@ts-sections/section-impact-cards/interfaces'
// hooks
import { useDefaultCtaUrl } from '@composables/tools/default-cta-url.hook'
import { useEventsSection } from '@hooks/events/sections/events-section.hook'

interface ImpactCardMotion {
  initial: { opacity: number, y: number }
  visibleOnce: { opacity: number, y: number, transition: { duration: number, delay: number } }
}

interface SectionImpactCardsHookOptions {
  section: () => SectionImpactCardsProps
}

interface SectionImpactCardsHookReturn {
  refTitle: ComputedRef<string>
  refButtonText: ComputedRef<string>
  refButtonVariant: ComputedRef<NonNullable<BlockSectionLayoutProps['buttonVariant']>>
  refHref: ComputedRef<string>
  refMainCard: ComputedRef<BlockCardImpactProps | null>
  refSideCards: ComputedRef<BlockCardIconProps[]>
  getCardMotion: (index: number) => ImpactCardMotion
  handleClickButton: () => void
}

export const useSectionImpactCards = ({ section }: SectionImpactCardsHookOptions): SectionImpactCardsHookReturn => {
  const refDefaultCtaUrl = useDefaultCtaUrl()
  const { buildCtaHref, trackSectionEvent } = useEventsSection({ sectionName: 'impact-cards' })

  const refLayout = computed<BlockSectionLayoutProps | null>(() => section().layout ?? null)

  const refTitle = computed<string>(() => refLayout.value?.title ?? '')
  const refButtonText = computed<string>(() => refLayout.value?.buttonText ?? '')

  const refButtonVariant = computed<NonNullable<BlockSectionLayoutProps['buttonVariant']>>(() => (
    refLayout.value?.buttonVariant ?? 'primary'
  ))

  const refHref = computed<string>(() => {
    const layout = refLayout.value
    const base = layout?.isDefaultUrl ? refDefaultCtaUrl.value : (layout?.buttonLink ?? '#')
    return buildCtaHref(base, { text: refButtonText.value })
  })

  const refMainCard = computed<BlockCardImpactProps | null>(() => section().mainCard ?? null)

  // The side cards are visually locked to the gray surface (the block fixes the look,
  // the editor only supplies content); shape and shapeColor stay editor-driven.
  const refSideCards = computed<BlockCardIconProps[]>(() => (
    (section().sideCards ?? []).map(card => ({ ...card, color: 'gray' }))
  ))

  const getCardMotion = (index: number): ImpactCardMotion => ({
    initial: { opacity: 0, y: 40 },
    visibleOnce: { opacity: 1, y: 0, transition: { duration: 500, delay: index * 120 } },
  })

  const handleClickButton = (): void => {
    trackSectionEvent('Button_click', { name: 'cta', text: refButtonText.value, url: refHref.value })
  }

  return {
    refTitle,
    refButtonText,
    refButtonVariant,
    refHref,
    refMainCard,
    refSideCards,
    getCardMotion,
    handleClickButton,
  }
}
